import { addError } from "@wayflyer/blackbox-fe";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type { CountryCode, GooglePlacesAutocompleteResponse } from "@wayflyer/flyui";

import { PLACES_API_CONFIG, DEFAULT_ADDRESS_TYPES } from "./google-places-constants";
import { usePlacesCache } from "./use-places-cache";
import type { PlaceDetails } from "./google-places";

export const usePlacesApi = () => {
  const queryClient = useQueryClient();
  const cache = usePlacesCache(queryClient);

  const fetchPlaceDetails = useCallback(
    async (placeId: string): Promise<PlaceDetails | null> => {
      try {
        const cached = cache.getCachedPlaceDetails(placeId);
        if (cached) {
          return cached;
        }

        const response = await axios.get(`${PLACES_API_CONFIG.placeDetailsUrl}/${placeId}`, {
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": PLACES_API_CONFIG.apiKey,
            "X-Goog-FieldMask": PLACES_API_CONFIG.fieldMask,
          },
        });

        cache.cachePlaceDetails(placeId, response.data);
        return response.data;
      } catch (err) {
        addError("Failed to fetch Google Places details", err);
        return null;
      }
    },
    [cache]
  );

  const fetchAutocomplete = useCallback(
    async (
      searchTerm: string,
      countries?: CountryCode[],
      minQueryLength?: number
    ): Promise<GooglePlacesAutocompleteResponse> => {
      if (!searchTerm || searchTerm.length < (minQueryLength || PLACES_API_CONFIG.defaultMinQueryLength)) {
        return {};
      }

      const body = {
        input: searchTerm,
        ...(countries?.length && {
          regionCode: countries[0],
          includedRegionCodes: countries,
        }),
        includedPrimaryTypes: DEFAULT_ADDRESS_TYPES,
      };

      const response = await axios.post(PLACES_API_CONFIG.autocompleteUrl, body, {
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": PLACES_API_CONFIG.apiKey,
        },
      });

      return response.data;
    },
    []
  );

  return { fetchPlaceDetails, fetchAutocomplete, cache };
};
